import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    accordionSummaryClasses,
    paletteColors,
    Typography,
} from '@evaneos/b2b-ui';
import ArrowRightIcon from '@evaneos/b2b-ui/icons/ArrowRight';
import DoneAll from '@evaneos/b2b-ui/icons/DoneAll';
import AssignmentOutlinedIcon from '@evaneos/b2b-ui/icons/AssignmentOutlined';
import BedOutlinedIcon from '@evaneos/b2b-ui/icons/BedOutlined';
import LanguageIcon from '@evaneos/b2b-ui/icons/LanguageOutlined';
import CarIcon from '@evaneos/b2b-ui/icons/DirectionsCarFilledOutlined';
import FlightIcon from '@evaneos/b2b-ui/icons/Flight';
import DirectionsWalkingIcon from '@evaneos/b2b-ui/icons/DirectionsWalkOutlined';
import ExploreIcon from '@evaneos/b2b-ui/icons/ExploreOutlined';

import { useGetDossierData, useGetTripProject } from '../../../resources/dossier/queries';
import { DossierId } from '../../../resources/dossier/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../shared/analytics';
import { DefinitionList } from '../../../design-system/DefinitionList/DefinitionList';

import { GlobalLoader } from '../../../shared/components/layout/GlobalLoader';
import { useDossierLayoutInfo } from '../DossierLayoutInfoContext';

import { AB_TravelRhythmItem } from './AB_TravelRhythmItem';
import { ParticipantsInfo } from './ParticipantsInfo';
import { RequestContext } from './RequestContext';
import { TripProjectErrorAlert } from './TripProjectErrorAlert';
import { TripProjectQuestionsAnswers } from './TripProjectQuestionsAnswers';

/* eslint-disable max-lines-per-function, complexity -- complex component with many conditional sections and data transformations */
export const InitialRequest = () => {
    const { formatMessage, formatDate } = useIntl();
    const { dossierId } = useParams() as { dossierId: DossierId };
    const { trackEvent } = useTrackEventAmplitude();

    const { isFirstColumnNarrow } = useDossierLayoutInfo();

    const { dossier, participants } = useGetDossierData(dossierId) || {};
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const marketCode = dossier?.project?.marketCode;
    const { data, status, error } = useGetTripProject(dossierId, marketCode);

    const requestDate = dossier?.requestDate;

    const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
        if (isExpanded) {
            trackEvent((ampli, defaultProperties) => {
                ampli.detailsInitialRequestClicked({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        }
    };

    return (
        <Accordion
            elevation={0}
            disableGutters
            defaultExpanded
            onChange={handleAccordionChange}
            sx={{
                '&::before': {
                    display: 'none',
                },
                [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
                    {
                        transform: 'rotate(90deg)',
                    },
                backgroundColor: paletteColors.neutral[100],
                borderRadius: 2,
                py: 1,
                px: 2,
            }}
        >
            <AccordionSummary
                expandIcon={<ArrowRightIcon htmlColor={paletteColors.neutral[700]} />}
                sx={{
                    flexDirection: 'row-reverse',
                    padding: 0,
                    gap: 1,
                    [`&.${accordionSummaryClasses.focusVisible}`]: {
                        backgroundColor: paletteColors.green[200],
                    },
                }}
            >
                <Typography variant="h4" component="strong">
                    {formatMessage({ id: 'dossier.information.initial_request.title' })}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
                {status === 'pending' && (
                    <Typography variant="body2" my={0.5}>
                        <GlobalLoader />
                    </Typography>
                )}

                {status === 'error' && error && <TripProjectErrorAlert error={error} />}

                {status === 'success' && (
                    <>
                        <DefinitionList data-testid="initial-request-main-info">
                            <DefinitionList.Item>
                                <DefinitionList.Label
                                    id="request-date"
                                    icon={<DoneAll fontSize="small" aria-hidden="true" />}
                                    iconOnly={isFirstColumnNarrow}
                                >
                                    {formatMessage({
                                        id: 'dossier.information.initial_request.received_date',
                                    })}
                                </DefinitionList.Label>
                                <DefinitionList.StaticValue>
                                    <Typography variant="body2">
                                        {formatDate(requestDate, {
                                            day: '2-digit',
                                            month: 'long',
                                            year: 'numeric',
                                            hour: 'numeric',
                                            minute: 'numeric',
                                        })}
                                    </Typography>
                                </DefinitionList.StaticValue>
                            </DefinitionList.Item>
                            <RequestContext tripProject={data} />
                            {participants && (
                                <ParticipantsInfo
                                    participants={participants}
                                    requesterAge={dossier?.project?.requesterAge}
                                />
                            )}
                            {data.projectStep && (
                                <DefinitionList.Item>
                                    <DefinitionList.Label
                                        id="project-step"
                                        icon={
                                            <AssignmentOutlinedIcon
                                                aria-hidden="true"
                                                fontSize="small"
                                            />
                                        }
                                        iconOnly={isFirstColumnNarrow}
                                    >
                                        {formatMessage({
                                            id: 'dossier.information.initial_request.project_step',
                                        })}
                                    </DefinitionList.Label>
                                    <DefinitionList.StaticValue>
                                        <Typography variant="body2">
                                            {data.projectStep.valueTranslated}
                                        </Typography>
                                    </DefinitionList.StaticValue>
                                </DefinitionList.Item>
                            )}
                            {data.productRange && (
                                <DefinitionList.Item>
                                    <DefinitionList.Label
                                        id="product-range"
                                        icon={<ExploreIcon fontSize="small" aria-hidden="true" />}
                                        iconOnly={isFirstColumnNarrow}
                                    >
                                        {formatMessage({
                                            id: 'dossier.information.initial_request.product_range',
                                        })}
                                    </DefinitionList.Label>
                                    <DefinitionList.StaticValue>
                                        <Typography variant="body2">
                                            {data.productRange.valueTranslated.join(', ')}
                                        </Typography>
                                    </DefinitionList.StaticValue>
                                </DefinitionList.Item>
                            )}
                            {data.travelPaces && (
                                <DefinitionList.Item>
                                    <DefinitionList.Label
                                        id="travel-paces"
                                        icon={
                                            <DirectionsWalkingIcon
                                                fontSize="small"
                                                aria-hidden="true"
                                            />
                                        }
                                        iconOnly={isFirstColumnNarrow}
                                    >
                                        {formatMessage({
                                            id: 'dossier.information.initial_request.travel_paces',
                                        })}
                                    </DefinitionList.Label>
                                    <DefinitionList.StaticValue>
                                        <Typography variant="body2">
                                            {data.travelPaces.valueTranslated.join(', ')}
                                        </Typography>
                                    </DefinitionList.StaticValue>
                                </DefinitionList.Item>
                            )}
                            {/* BEGIN - AB ab_travel_rhythm - PR #TODO_AFTER_PR_CREATION_ab_travel_rhythm */}
                            <AB_TravelRhythmItem
                                value={data?.experiment?.travelRhythm}
                                isFirstColumnNarrow={isFirstColumnNarrow}
                            />
                            {/* END - AB ab_travel_rhythm - PR #TODO_AFTER_PR_CREATION_ab_travel_rhythm */}
                            {data.flightStatus && (
                                <DefinitionList.Item>
                                    <DefinitionList.Label
                                        id="flight-status"
                                        icon={
                                            <FlightIcon
                                                fontSize="small"
                                                aria-hidden="true"
                                                sx={{ transform: 'rotate(45deg)' }}
                                            />
                                        }
                                        iconOnly={isFirstColumnNarrow}
                                    >
                                        {formatMessage({
                                            id: 'dossier.information.initial_request.flight_status',
                                        })}
                                    </DefinitionList.Label>
                                    <DefinitionList.StaticValue>
                                        <Typography variant="body2">
                                            {data.flightStatus.valueTranslated}
                                        </Typography>
                                    </DefinitionList.StaticValue>
                                </DefinitionList.Item>
                            )}
                            {data.hosting && (
                                <DefinitionList.Item>
                                    <DefinitionList.Label
                                        id="hosting"
                                        icon={
                                            <BedOutlinedIcon fontSize="small" aria-hidden="true" />
                                        }
                                        iconOnly={isFirstColumnNarrow}
                                    >
                                        {formatMessage({
                                            id: 'dossier.information.initial_request.hosting',
                                        })}
                                    </DefinitionList.Label>
                                    <DefinitionList.StaticValue>
                                        {data.hosting.valueTranslated.map((value) => (
                                            <Typography key={value} variant="body2">
                                                {value}
                                            </Typography>
                                        ))}
                                    </DefinitionList.StaticValue>
                                </DefinitionList.Item>
                            )}
                            {data.accompaniment && (
                                <DefinitionList.Item>
                                    <DefinitionList.Label
                                        id="accompaniment"
                                        icon={<CarIcon fontSize="small" aria-hidden="true" />}
                                        iconOnly={isFirstColumnNarrow}
                                    >
                                        {formatMessage({
                                            id: 'dossier.information.initial_request.accompaniment',
                                        })}
                                    </DefinitionList.Label>
                                    <DefinitionList.StaticValue>
                                        <Typography variant="body2">
                                            {data.accompaniment.valueTranslated}
                                        </Typography>
                                    </DefinitionList.StaticValue>
                                </DefinitionList.Item>
                            )}
                            {data.guideLanguage && (
                                <DefinitionList.Item>
                                    <DefinitionList.Label
                                        id="guide-language"
                                        icon={<LanguageIcon fontSize="small" aria-hidden="true" />}
                                        iconOnly={isFirstColumnNarrow}
                                    >
                                        {formatMessage({
                                            id: 'dossier.information.initial_request.guide_language',
                                        })}
                                    </DefinitionList.Label>
                                    <DefinitionList.StaticValue>
                                        <Typography variant="body2">
                                            {data.guideLanguage.valueTranslated.join(', ')}
                                        </Typography>
                                    </DefinitionList.StaticValue>
                                </DefinitionList.Item>
                            )}
                        </DefinitionList>
                        <TripProjectQuestionsAnswers tripProject={data} />
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
};
/* eslint-enable max-lines-per-function, complexity */
